<template>
  <section class="app-main">
    <!-- 主要内容区域 -->
    <div class="card-container">
      <router-view v-slot="{ Component, route }">
        <!-- AI 覆盖层使用无缝同时过渡（undefined），普通路由切换保留 out-in 以防布局错位 -->
        <transition :name="transitionName" :mode="['agent-in', 'agent-out'].includes(transitionName) ? undefined : 'out-in'">
          <!-- 必须用单根元素包裹：Vue Transition 的 mode="out-in" 要求单根，否则多根组件会导致切换后白屏 -->
          <!-- AI 路由时加 --ai-overlay，使内容从左上角全屏覆盖弹出，而非路由内部 -->
          <div v-if="Component" :key="routeViewKey" class="router-view-wrapper"
            :class="{ 'router-view-wrapper--ai-overlay': isAiPath(route.path) }">
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

// 路由切换动画名称
const transitionName = ref('fade')

// // 判断路径是否为 AI 路由
const isAiPath = (path) => path === '/ai' || path.startsWith('/ai/')

const routeViewKey = computed(() => {
  const params = new URLSearchParams()
  Object.entries(route.query).forEach(([key, value]) => {
    if (key === 'tab' || key === 'deviceId' || key === 'stationId') return
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)))
      return
    }
    if (value != null) {
      params.set(key, String(value))
    }
  })
  const queryWithoutTab = params.toString()
  return `${route.path}${queryWithoutTab ? `?${queryWithoutTab}` : ''}${route.hash || ''}`
})

watch(() => router.currentRoute.value, async (newRoute, oldRoute) => {
  const newPath = newRoute.path
  const oldPath = oldRoute.path

  // 仅对一级路由切换添加动画
  const newPath_l1 = newPath.split('/')[1]
  const oldPath_l1 = oldPath.split('/')[1]
  if (newPath_l1 === oldPath_l1) {
    transitionName.value = ''
  } else {
    if (isAiPath(newPath) && !isAiPath(oldPath)) {
      transitionName.value = 'agent-in'
    } else if (!isAiPath(newPath) && isAiPath(oldPath)) {
      transitionName.value = 'agent-out'
    } else {
      transitionName.value = 'scale'
    }
  }

  await nextTick()

})

</script>

<style lang="scss" scoped>
.app-main {
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
}

.card-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.router-view-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* AI 路由：全屏覆盖层，从左上角展开，覆盖整个视口（含 Navbar） */
.router-view-wrapper--ai-overlay {
  position: fixed !important;
  inset: 0 !important;
  z-index: 100;
  width: 100vw !important;
  height: 100vh !important;
  /* 增加高亮边缘和深邃阴影。全屏时在视口外不可见，缩小动画时完美勾勒出页面轮廓 */
  box-shadow: 0 10px 4cap rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15);
  background-color: var(--el-bg-color, #fff);
  /* 确保背景不透明，让边缘和阴影更清晰 */
}

// scale 缩放动画
.scale-enter-active,
.scale-leave-active {
  transition: all 0.5s ease;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

// 默认淡入淡出过渡动画（非 AI 页面路由切换使用）
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 智能体进入动画（业务系统 -> 智能体）
.agent-in-enter-active,
.agent-in-leave-active {
  /* 弹出时：增加圆角和滤镜的丝滑过渡，加长到 0.9s 让展开更从容 */
  transition: all 0.9s cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: 30px 30px;
}

.agent-in-enter-from {
  opacity: 0;
  transform: scale(0.01);
  filter: blur(20px);
  border-radius: 100px; /* 从图标弹出时是一个圆润的形态 */
}

.agent-in-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

// 智能体退出动画（智能体 -> 业务系统）
.agent-out-enter-active,
.agent-out-leave-active {
  /* 吸入时：带有引力加速度的缩放，加长到 0.8s 让吸入感更明显 */
  transition: all 0.8s cubic-bezier(0.5, 0, 0, 1);
  transform-origin: 30px 30px;
}

.agent-out-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.agent-out-leave-to {
  opacity: 0;
  transform: scale(0.01);
  filter: blur(25px);
  /* 加深模糊度，彻底融化页面内容，避免看清微缩的顶部图标 */
  border-radius: 100px;
  /* 缩小进图标时变成一个圆点，更像 App 收起 */
}
</style>
